/** @format */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { camelCase, camelCaseTransformMerge } from 'change-case';
import { stringify } from 'qs';

import { toSnakeCase } from '@toucan/common/helpers/change-case';

export function toCamelCase<T = string>(key: string): T {
    return camelCase(key, {
        transform: camelCaseTransformMerge,
    }) as T;
}

/**
 * Recursively convert the key names of an object.
 *
 * @param object the object to convert
 * @param convert the conversion function
 */
export function convertKeyNames(
    object: any,
    convert: (name: string) => string
): any {
    if (object == null || typeof object !== 'object') {
        return object;
    }

    if (Array.isArray(object)) {
        return object.map((value) => convertKeyNames(value, convert));
    } else {
        const converted = {};
        Object.keys(object).forEach((key) => {
            if (Object.keys(object).includes(key)) {
                // @ts-ignore - Element implicitly has an any type - Touch and Change
                converted[convert(key)] = convertKeyNames(object[key], convert);
            }
        });
        return converted;
    }
}

function requestFulfilled(request: AxiosRequestConfig): AxiosRequestConfig {
    // if we have no data then we can just return the original request
    if (!request.data) {
        return request;
    }

    // convert our data keys to snake case
    const data = convertKeyNames(request.data, toSnakeCase);

    // if we need form data instead of json then we need to stringify our data
    if (
        request.headers?.['Content-Type'] ===
        'application/x-www-form-urlencoded'
    ) {
        request.data = stringify(data, {
            encodeValuesOnly: true,
            arrayFormat: 'repeat',
        });
        return request;
    }

    // add our converted json data to the request and return it
    request.data = data;
    return request;
}

function responseFulfilled(response: AxiosResponse): AxiosResponse {
    response.data = convertKeyNames(response.data, toCamelCase);
    return response;
}

function responseRejected(error: any) {
    if (!axios.isCancel(error) && error?.response?.data) {
        error.response.data = convertKeyNames(error.response.data, toCamelCase);
    }
    return Promise.reject(error);
}

export const changeCaseInterceptor = {
    requestFulfilled,
    responseFulfilled,
    responseRejected,
};
